import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Save, Bell } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

const DAYS = [
  { v: 0, l: 'Monday' },
  { v: 1, l: 'Tuesday' },
  { v: 2, l: 'Wednesday' },
  { v: 3, l: 'Thursday' },
  { v: 4, l: 'Friday' },
  { v: 5, l: 'Saturday' },
  { v: 6, l: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
  return { v: i, l: ampm };
});

export default function WeeklyHoursReminderDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [dayOfWeek, setDayOfWeek] = useState(4);
  const [hour, setHour] = useState(17);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/me/weekly-hours-reminder`)
      .then((res) => {
        const s = res.data || {};
        setEnabled(!!s.enabled);
        setDayOfWeek(Number.isInteger(s.day_of_week) ? s.day_of_week : 4);
        setHour(Number.isInteger(s.hour) ? s.hour : 17);
      })
      .catch(() => toast.error('Failed to load reminder settings'))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/me/weekly-hours-reminder`, {
        enabled, day_of_week: dayOfWeek, hour,
      });
      toast.success(enabled ? 'Weekly reminder turned ON' : 'Weekly reminder turned OFF');
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="weekly-hours-reminder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Bell className="w-5 h-5 text-orange-500" />
            Weekly Hours Reminder
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Get an email reminder once a week to submit your hours.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-4 mt-2">
            {/* Enable toggle */}
            <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-100 rounded-lg">
              <div>
                <Label className="font-semibold text-gray-900">Email me a weekly reminder</Label>
                <p className="text-xs text-gray-500 mt-0.5">Sent to your account email address.</p>
              </div>
              <button
                type="button"
                onClick={() => setEnabled(!enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-orange-500' : 'bg-gray-300'}`}
                data-testid="reminder-enabled-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1.5 block">Day of week</Label>
              <Select value={String(dayOfWeek)} onValueChange={(v) => setDayOfWeek(parseInt(v, 10))}>
                <SelectTrigger className="border-2 border-gray-300 h-11 text-base" data-testid="reminder-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-1.5 block">Time of day (Eastern)</Label>
              <Select value={String(hour)} onValueChange={(v) => setHour(parseInt(v, 10))}>
                <SelectTrigger className="border-2 border-gray-300 h-11 text-base" data-testid="reminder-hour-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h.v} value={String(h.v)}>{h.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold min-h-[44px]"
              data-testid="save-reminder-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving…' : 'Save Reminder'}
            </Button>

            <div className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
              <Mail className="w-3 h-3" />
              Reminders use Eastern Time.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
